import type { Activity, DownloadAdapter, DownloadIdentity, DownloadLocator, StartDownload } from './types.ts';
import { randomUuid } from '@/lib/uuid';

type Options = { adapters: Partial<Record<DownloadLocator['kind'],DownloadAdapter>>; load: () => string | null; save: (value: string) => void; schedule: (callback: () => void | Promise<void>, ms: number) => unknown; cancel: (timer: unknown) => void; handoff: (url: string, filename: string) => void };
const terminal = (a: Activity) => a.snapshot?.remoteState === 'ready' || a.snapshot?.remoteState === 'failed';
function locator(value: unknown): DownloadLocator | undefined {
  if (!value || typeof value !== 'object') return;
  const x = value as Record<string,unknown>;
  if (typeof x.project !== 'string' || x.project.length > 256) return;
  if (x.kind === 'model_export' && typeof x.jobId === 'string') {
    const result: Extract<DownloadLocator,{kind:'model_export'}>={kind:x.kind,project:x.project,jobId:x.jobId};
    if(typeof x.model==='string'&&x.model.length<=300&&typeof x.version==='string'&&x.version.length<=200){result.model=x.model;result.version=x.version;}
    return result;
  }
  if (x.kind === 'dataset_archive' && typeof x.id === 'string') return { kind:x.kind, project:x.project, id:x.id };
  if (x.kind === 'dataset_archive_request' && typeof x.requestKey === 'string') return {kind:x.kind,project:x.project,requestKey:x.requestKey};
  if (x.kind === 'view_export' && typeof x.viewId === 'string' && ['yolo','coco','imagefolder'].includes(String(x.format))) {
    const result: DownloadLocator = {kind:x.kind,project:x.project,viewId:x.viewId,format:String(x.format)};
    for (const field of ['exportId','materializeJobId','treeId'] as const) if (typeof x[field] === 'string') result[field] = x[field];
    return result;
  }
}
export class DownloadStore {
  private options: Options;
  private activities: Activity[] = [];
  private listeners = new Set<() => void>();
  private controls = new Map<string,{ abort: AbortController; timer?: unknown; failures: number; handingOff?: boolean }>();
  private disposed = false;
  constructor(options: Options) {
    this.options = options;
    try {
      const saved: unknown = JSON.parse(options.load() ?? '[]');
      if (Array.isArray(saved)) for (const value of saved.slice(-20)) {
        const pin = locator(value.locator);
        if (!pin || typeof value.key !== 'string' || !value.identity || typeof value.identity.title !== 'string' || typeof value.identity.detail !== 'string') continue;
        this.activities.push({key:value.key.slice(0,200),identity:{title:value.identity.title.slice(0,300),detail:value.identity.detail.slice(0,500),group:typeof value.identity.group==='string'?value.identity.group.slice(0,500):undefined},locator:pin,phase:'observing',hidden:!!value.hidden,inline:false,autoHandoff:false});
        this.controls.set(value.key,{abort:new AbortController(),failures:0});

      }
    } catch { /* Recovery is optional when session storage is unavailable. */ }
  }
  getSnapshot = () => this.activities;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => {this.listeners.delete(listener)}; };
  private changed() {
    this.activities = [...this.activities];
    try {this.options.save(JSON.stringify(this.activities.filter(a=>a.locator).slice(-20).map(a=>({key:a.key,identity:a.identity,locator:a.locator,hidden:a.hidden}))))} catch { /* Private browsing. */ }
    this.listeners.forEach(f=>f());
  }
  private item(key: string) { return this.activities.find(a=>a.key===key); }
  private schedule(key: string, ms=2500) {
    const c=this.controls.get(key);
    if (c && !this.disposed) c.timer=this.options.schedule(()=>this.observe(key),ms);
  }
  private adapter(pin: DownloadLocator) {
    const value=this.options.adapters[pin.kind];
    if (!value) throw new Error('This download is unavailable in this session.');
    return value;
  }
  async start(input: StartDownload) {
    const existing=this.item(input.key);
    if (existing && !terminal(existing) && existing.phase!=='interrupted') {this.show(input.key);return input.key;}
    if (existing) this.remove(input.key);
    const activity: Activity={key:input.key,identity:input.identity,locator:input.initialLocator,phase:'requesting',hidden:false,inline:input.inline!==false,autoHandoff:input.autoHandoff!==false};
    this.activities.push(activity);
    const control={abort:new AbortController(),failures:0};this.controls.set(input.key,control);this.changed();
    try {
      const pin=await input.prepare();
      if (this.disposed || this.controls.get(input.key)!==control) return input.key;
      activity.locator=pin;activity.phase='observing';this.changed();await this.observe(input.key);
    } catch (error) {
      if (this.disposed || this.controls.get(input.key)!==control) return input.key;
      activity.message=(error as Error).message;
      if (activity.locator) {activity.phase='reconnecting';this.schedule(input.key,2500)}
      else activity.phase='interrupted';
      this.changed();
    }
    return input.key;
  }
  private async observe(key: string) {
    const activity=this.item(key),control=this.controls.get(key);
    if (!activity?.locator || !control || this.disposed) return;
    try {
      const snapshot=await this.adapter(activity.locator).observe(activity.locator,control.abort.signal);
      if (this.disposed || this.controls.get(key)!==control) return;
      const old=activity.snapshot;
      if (old && (snapshot.attempt<old.attempt || (snapshot.attempt===old.attempt && old.progress && snapshot.progress && snapshot.progress.sequence<old.progress.sequence))) {this.schedule(key);return;}
      activity.snapshot=snapshot;activity.locator=snapshot.locator;activity.phase='observing';activity.message=undefined;control.failures=0;this.changed();
      if (!terminal(activity)) this.schedule(key);
      else if (snapshot.remoteState==='ready' && activity.autoHandoff) {activity.autoHandoff=false;await this.handoff(key);}
    } catch (error) {
      if (this.disposed || control.abort.signal.aborted || this.controls.get(key)!==control) return;
      const status=(error as {status?:number}).status;
      activity.message=(error as Error).message;
      if (status && status>=400 && status<500) activity.phase='interrupted';
      else {activity.phase='reconnecting';this.schedule(key,[5000,10000,20000,30000][Math.min(control.failures++,3)]);}
      this.changed();
    }
  }
  async retry(key: string) {
    const activity=this.item(key);if (!activity?.locator || activity.phase==='requesting')return;
    const c=this.controls.get(key);if(c?.timer)this.options.cancel(c.timer);
    c?.abort.abort();const control={abort:new AbortController(),failures:0};this.controls.set(key,control);
    activity.phase='requesting';activity.message=undefined;this.changed();
    try {
      activity.locator=activity.snapshot?.canRetry ? await this.adapter(activity.locator).retry(activity.locator) : activity.locator;
      if (this.disposed || this.controls.get(key)!==control)return;
      activity.snapshot=undefined;activity.autoHandoff=true;await this.observe(key);
    } catch(error){activity.phase='interrupted';activity.message=(error as Error).message;this.changed();}
  }
  async handoff(key: string) {
    const a=this.item(key),c=this.controls.get(key);if (!a?.locator || !c || c.handingOff)return;
    c.handingOff=true;
    try {
      const grant=await this.adapter(a.locator).access(a.locator);
      if (this.disposed || this.controls.get(key)!==c)return;
      if (Object.keys(grant.headers).length) throw new Error('Use the CLI to download this file with its required headers.');
      const url=new URL(grant.url,typeof location==='undefined'?'http://localhost':location.href);
      if (!['http:','https:'].includes(url.protocol)) throw new Error('The download URL is invalid.');
      a.phase='handed_to_browser';a.autoHandoff=false;a.message=undefined;this.changed();
      this.options.handoff(grant.url,grant.filename);
    } catch(error){
      if(!this.disposed){
        a.autoHandoff=false;
        if((error as {status?:number}).status===410){await this.observe(key);return;}
        a.phase='interrupted';a.message=(error as Error).message;this.changed();
      }
    }
    finally {c.handingOff=false;}
  }
  show(key:string){const a=this.item(key);if(a){a.hidden=false;this.changed();}}
  hide(key:string){const a=this.item(key);if(a){a.hidden=true;this.changed();}}
  inline(key:string,value:boolean){const a=this.item(key);if(a && a.inline!==value){a.inline=value;this.changed();}}
  private remove(key:string){const c=this.controls.get(key);c?.abort.abort();if(c?.timer)this.options.cancel(c.timer);this.controls.delete(key);this.activities=this.activities.filter(a=>a.key!==key);}
  recordHandoff(identity:DownloadIdentity){
    this.activities.push({key:`direct:${randomUuid()}`,identity,phase:'handed_to_browser',hidden:false,inline:false,autoHandoff:false});
    this.activities=this.activities.slice(-20);this.changed();
  }
  async trackLocalDownload(input:{key:string;identity:DownloadIdentity;prepare:()=>Promise<void>}){
    const old=this.item(input.key);if(old?.phase==='requesting')return;
    if(old)this.remove(input.key);
    const a:Activity={key:input.key,identity:input.identity,phase:'requesting',hidden:false,inline:false,autoHandoff:false};
    this.activities.push(a);this.changed();
    try{await input.prepare();if(!this.disposed){a.phase='handed_to_browser';this.changed()}}
    catch(error){if(!this.disposed){a.phase='interrupted';a.message=(error as Error).message;this.changed()}throw error}
  }
  clear(){for(const a of this.activities)this.remove(a.key);this.activities=[];this.changed();}
  resume(){this.disposed=false;for(const a of this.activities){if(!terminal(a) && a.locator){this.controls.set(a.key,{abort:new AbortController(),failures:0});this.schedule(a.key,0)}}}
  pause(){this.disposed=true;for(const c of this.controls.values()){c.abort.abort();if(c.timer)this.options.cancel(c.timer)}this.controls.clear();}
  dispose(){this.disposed=true;for(const a of this.activities)this.remove(a.key);this.activities=[];this.listeners.forEach(f=>f());this.listeners.clear();}
}

export function isPlainDownloadClick(event:{button:number;ctrlKey?:boolean;metaKey?:boolean;shiftKey?:boolean;altKey?:boolean;defaultPrevented?:boolean}){
  return event.button===0&&!event.ctrlKey&&!event.metaKey&&!event.shiftKey&&!event.altKey&&!event.defaultPrevented;
}
